import { useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog"
import { Button } from "../ui/button"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { type VisitRequest, useNavigate } from "@/lib/page-rendering"

interface TransactionDialogContentProps {
  initDescription: string
  pendingDescription: string
  successDescription: string
  errorDescription: string
  // The request the confirmation performs, at `route`.
  options: Omit<VisitRequest, "href">
  route: string
}
export type ConfirmationState = "initial" | "pending" | "success" | "error"

export default function TranscationDialog({
  children,
  ...props
}: TransactionDialogContentProps & { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [confirmationState, setConfirmationState] =
    useState<ConfirmationState>("initial")

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {children}
      <TransactionDialogContent<ConfirmationState>
        {...props}
        closeDialog={() => {
          setOpen(false)
          setConfirmationState("initial")
        }}
        onConfirmationChange={setConfirmationState}
        confirmationState={confirmationState}
      />
    </AlertDialog>
  )
}

export function TransactionDialogContent<T>({
  initDescription,
  pendingDescription,
  successDescription,
  errorDescription,
  route,
  options,
  closeDialog,
  onConfirmationChange,
  confirmationState,
  children,
  ...rest
}: TransactionDialogContentProps & {
  closeDialog: () => void
  children?: React.ReactNode
  onConfirmationChange: (confirmationState: ConfirmationState) => void
  confirmationState: T
} & Omit<React.ComponentProps<typeof AlertDialogContent>, "onEscapeKeyDown">) {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const handleConfirm = () => {
    onConfirmationChange("pending")
    navigate({ ...options, href: route, preserveScroll: true }).then(
      ({ errors }) =>
        onConfirmationChange(
          Object.keys(errors).length === 0 ? "success" : "error",
        ),
    )
  }
  return (
    <AlertDialogContent
      {...rest}
      data-confirmationstate={confirmationState}
      onEscapeKeyDown={(e) => {
        if (confirmationState !== "pending") return
        e.preventDefault()
      }}
    >
      {confirmationState === "initial" && (
        <>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("confirmation_dialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>{initDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("actions.cancel")}</AlertDialogCancel>
            <Button onClick={handleConfirm}>{t("actions.confirm")}</Button>
          </AlertDialogFooter>
        </>
      )}
      {confirmationState === "pending" && (
        <>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("confirmation_dialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="flex flex-row items-center gap-2">
              <Loader2 className="animate-spin" />
              {pendingDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </>
      )}
      {confirmationState === "success" && (
        <>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("actions.done")}</AlertDialogTitle>
            <AlertDialogDescription data-testid="dialog-success">
              {successDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={closeDialog}>
              {t("actions.close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </>
      )}
      {confirmationState === "error" && (
        <>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("errors.unexpected")}</AlertDialogTitle>
            <AlertDialogDescription data-testid="dialog-error">
              {errorDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={closeDialog}>
              {t("actions.close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </>
      )}
      {children}
    </AlertDialogContent>
  )
}
export function TransactionDialogTrigger({
  children,
}: {
  children: React.ReactNode
}) {
  return <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
}
